import React, { useEffect, useState } from "react";
import { Loader2, Save, BookOpen, Trash2, Edit, Plus, RefreshCw, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { useLocation, useParams } from "react-router-dom";
import axios from "../../api/axios";

// Import your unified PaginationSearchbar component
import PaginationSearchbar from "../../GlobalComponents/PaginationSearchbar";

// Import central theme classes from MasterCssClass.js
import {
  masterFormCard,
  masterFormFieldGroup,
  masterTableHead,
  masterInputLabel,
  masterInput,
  masterSelect,
  masterFormBtnSubmit,
  masterBtnBack,
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterBadgeLive,
  masterBadgeInactive,
  masterActionBtnEdit,
  masterActionBtnDelete,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterHeaderActions,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterThStatus,
  masterThManage,
  masterTdCode,
  masterTdName,
  masterTdStatus,
  masterTdManage,
  masterTdManageFlex,
  masterTdEmpty,
} from "../../GlobalComponents/MasterCssClass";

const ReferralCodeGenerate = () => {
  const location = useLocation();
  const { id } = useParams();

  const isNew = location.pathname.endsWith("/new");
  const isEdit = Boolean(id) || location.pathname.includes("/edit");

  const [viewMode, setViewMode] = useState(isNew || isEdit ? "form" : "form");
  const [editId, setEditId] = useState(id || null);
  const [referrals, setReferrals] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // List filter and pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    referralCode: "",
    fromDate: "",
    toDate: "",
    status: "ACTIVE",
  });

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/master/referral/all");
      const list = res.data?.data || [];
      setReferrals(list);

      if (id) {
        const row = list.find((r) => String(r._id) === String(id));
        if (row) {
          setFormData({
            referralCode: row.referralCode || "",
            fromDate: row.fromDate || "",
            toDate: row.toDate || "",
            status: row.status || "ACTIVE",
          });
          setEditId(row._id);
        }
      }
    } catch {
      toast.error("FAILED TO LOAD REFERRALS");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, [id]);

  useEffect(() => {
    if (location.pathname.endsWith("referral-master")) {
      setViewMode("form");
    }
  }, [location.pathname]);

  const resetForm = () => {
    setEditId(null);
    setFormData({
      referralCode: "",
      fromDate: "",
      toDate: "",
      status: "ACTIVE",
    });
  };

const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return; // Prevent multiple submissions if already saving

    const codeInput = formData.referralCode.trim().toUpperCase();
    if (!codeInput) return toast.error("REFERRAL CODE IS REQUIRED");

    setIsSaving(true);
    try {
      const payload = {
        id: editId || undefined,
        referralCode: codeInput,
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        status: (formData.status || "ACTIVE").toUpperCase(),
      };

      const res = await axios.post("/master/referral/upsert", payload);
      if (res.data.success) {
        toast.success(editId ? "RECORD UPDATED SUCCESSFULLY" : "RECORD SAVED SUCCESSFULLY", { id: "unique-save-toast" });
        fetchReferrals();
        resetForm();
        setViewMode("list");
      }
    } catch (err) {
      const serverMessage = err?.response?.data?.message || "";
      toast.error(serverMessage || "SAVE FAILED - CHECK SERVER", { id: "unique-error-toast" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditId(item._id);
    setFormData({
      referralCode: item.referralCode || "",
      fromDate: item.fromDate || "",
      toDate: item.toDate || "",
      status: item.status || "ACTIVE",
    });
    setViewMode("form");
  };

  const handleDelete = async (recordId) => {
    if (window.confirm("ARE YOU SURE YOU WANT TO DELETE THIS RECORD?")) {
      try {
        await axios.delete(`/master/referral/${recordId}`);
        toast.success("DELETED SUCCESSFULLY");
        fetchReferrals();
      } catch {
        toast.error("DELETE FAILED");
      }
    }
  };

  const filteredData = referrals.filter((item) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const startsWithFirstLetter = (value) => {
      if (!value) return false;
      return value.trim().toLowerCase().startsWith(term);
    };

    return (
      startsWithFirstLetter(item.referralId) ||
      startsWithFirstLetter(item.referralCode) ||
      startsWithFirstLetter(item.status || "ACTIVE")
    );
  });

  const isAll = rowsPerPage === "ALL" || rowsPerPage === -1;
  const startIndex = isAll ? 0 : (currentPage - 1) * rowsPerPage;
  const paginatedData = isAll
    ? filteredData
    : filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar`}>
      {viewMode === "form" ? (
        <div>
          <div className={masterPanelCard}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <BookOpen size={13} />
                </div>
                <h1 className={masterHeadingMain}>{editId ? "Edit Referral Code" : "Referral Code Form"}</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setViewMode("list");
                  }}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>VIEW LIST</span>
                </button>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className={`${masterFormCard} flex flex-col gap-2 mt-3`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 w-full">
              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Referral Code</label>
                <input
                  autoFocus
                  type="text"
                  value={formData.referralCode}
                  onChange={(e) =>
                    setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })
                  }
                  placeholder="ENTER REFERRAL CODE"
                  className={masterInput}
                  required
                />
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>From Date (dd-mm-yyyy)</label>
                <input
                  type="text"
                  value={formData.fromDate}
                  onChange={(e) =>
                    setFormData({ ...formData, fromDate: e.target.value })
                  }
                  placeholder="dd-mm-yyyy"
                  className={masterInput}
                />
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>To Date (dd-mm-yyyy)</label>
                <input
                  type="text"
                  value={formData.toDate}
                  onChange={(e) =>
                    setFormData({ ...formData, toDate: e.target.value })
                  }
                  placeholder="dd-mm-yyyy"
                  className={masterInput}
                />
              </div>

              <div className={masterFormFieldGroup}>
                <label className={masterInputLabel}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className={masterSelect}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between w-full pt-1">
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setViewMode("list");
                }}
                className={`${masterBtnBack} flex items-center justify-center gap-1.5 h-[26px]`}
              >
                <ArrowLeft size={14} />
                <span>BACK</span>
              </button>

              <div>
                <button
                  disabled={isSaving || loading}
                  type="submit"
                  className={masterFormBtnSubmit}
                >
                  {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {editId ? "UPDATE" : "SAVE"}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex flex-col flex-1 h-full">
          <div className={masterPanelCard}>
            <div className={masterHeaderContent}>
              <div className={masterHeaderBrand}>
                <div className={masterHeaderIconBox}>
                  <BookOpen size={13} />
                </div>
                <h1 className={masterHeadingMain}>Referral Code List</h1>
              </div>

              <div className={masterHeaderActions}>
                <button
                  type="button"
                  onClick={fetchReferrals}
                  className={masterBtnBack}
                  title="Refresh Records"
                >
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                  <span>SYNC</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setViewMode("form");
                  }}
                  className={masterFormBtnSubmit}
                >
                  <Plus size={12} strokeWidth={3} />
                  <span>ADD NEW</span>
                </button>
              </div>
            </div>
          </div>

          <PaginationSearchbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="SEARCH REFERRAL ID, CODE, OR STATUS..."
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
            totalItems={filteredData.length}
          />

          <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col`}>
            <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-260px)]`}>
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className={masterTableHead}>
                    <th className={masterThCode}>SL NO</th>
                    <th className={masterThName}>CODE</th>
                    <th className={masterThName}>FROM DATE</th>
                    <th className={masterThName}>TO DATE</th>
                    <th className={masterThStatus}>STATUS</th>
                    <th className={masterThManage}>MANAGE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className={masterTdEmpty}>
                        Syncing Database...
                      </td>
                    </tr>
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <tr key={item._id} className="border-b hover:bg-slate-50">
                        <td className={masterTdCode}>{startIndex + index + 1}</td>
                        <td className={masterTdName}>{item.referralCode || "N/A"}</td>
                        <td className={masterTdName}>{item.fromDate || "N/A"}</td>
                        <td className={masterTdName}>{item.toDate || "N/A"}</td>
                        <td className={masterTdStatus}>
                          <span
                            className={
                              item.status === "INACTIVE"
                                ? masterBadgeInactive
                                : masterBadgeLive
                            }
                          >
                            {item.status || "ACTIVE"}
                          </span>
                        </td>
                        <td className={masterTdManage}>
                          <div className={masterTdManageFlex}>
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className={masterActionBtnEdit}
                              title="Edit"
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item._id)}
                              className={masterActionBtnDelete}
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className={masterTdEmpty}>
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralCodeGenerate;